import type { TeamBudgetSessionFields } from '@sportninja/common/features/registration/teamBudgetPayment/types'
import { readSessionFieldError } from '@sportninja/common/features/registration/teamBudgetPayment/utils/checkoutErrors'
import {
  goesToTeamBudgetPaymentStep,
  offersTeamBudgetPayment,
  teamBudgetDepositCents,
} from '@sportninja/common/features/registration/teamBudgetPayment/utils/teamBudgetDeposit'
import { teamBudgetEntryChoiceCents } from '@sportninja/common/features/registration/teamBudgetPayment/utils/teamBudgetSessionPricing'

const CHOICE_FIELD = 'team_budget_payment_cents'

/**
 * SN-8441 / SN-8442 — what step 3 sends with the payment step on its way to step 4:
 * a Team Budget rep's choice, so the step arrives priced — their recorded choice, else
 * the deposit; Not now (null) when paying isn't offered. Nothing for anyone else, or
 * for an api2 without the checkout.
 */
export const paymentStepEntryParams = ({
  isTeamRep,
  division,
  hasPaymentProvider,
  session,
}: {
  isTeamRep?: boolean | null
  division?: Parameters<typeof teamBudgetDepositCents>[0]
  hasPaymentProvider: boolean
  session?: TeamBudgetSessionFields | null
}): { team_budget_payment_cents?: number | null } => {
  const cents = teamBudgetEntryChoiceCents({
    isTeamBudgetRep: goesToTeamBudgetPaymentStep({ isTeamRep, division }),
    offersPayment: offersTeamBudgetPayment({
      isTeamRep,
      division,
      hasPaymentProvider,
    }),
    depositCents: teamBudgetDepositCents(division),
    session,
  })

  return cents === undefined ? {} : { team_budget_payment_cents: cents }
}

/**
 * The server refused the entry choice ITSELF (its amount — below the provider's
 * minimum, say): the rep still moves on, and step 4 shows it on the amount field with
 * every other choice usable. Any other refusal keeps them on step 3.
 */
export const isEntryChoiceRefusal = (error: unknown): boolean =>
  readSessionFieldError(error, CHOICE_FIELD) !== null
